/**
 * ContractTermsPanel — the clauses of the agreement this proposal negotiates away from standard.
 *
 * The Agreement for Architectural Services is otherwise fixed text, identical on every proposal.
 * Today exactly one clause is negotiable here: Article 3.6, the initial payment due on execution,
 * which the agreement has always stated as 10% of the lump sum fees.
 *
 * Invisible in effect until used: a proposal with no row here prints the standard sentence,
 * word for word, exactly as every proposal printed before this panel existed.
 *
 * Deliberately NOT part of the all-inclusive fee panel. A negotiated initial payment has nothing
 * to do with how the fee was derived — it applies just as well to a standard derived-fee proposal,
 * and burying it inside the all-inclusive panel would make it reachable only by proposals that
 * happen to price that way.
 *
 * See docs/ALL_INCLUSIVE_FEE.md.
 */
import { useEffect, useState } from 'react'
import { F } from './CrmModal.jsx'
import { alertDialog } from '../Dialog.jsx'
import {
  DEFAULT_INITIAL_PAYMENT_PCT,
  getContractTerms, saveContractTerms, clearContractTerms,
} from '../../services/proposalContractTerms.js'

/** Matches api/_proposalContractTerms.js#formatPercent so the preview below is the real sentence. */
function fmtPct(v) {
  const n = Number(v)
  return isFinite(n) ? `${n}%` : `${DEFAULT_INITIAL_PAYMENT_PCT}%`
}

export default function ContractTermsPanel({ proposalId, retainerAmount }) {
  const [loading, setLoading] = useState(true)
  const [pct, setPct] = useState(String(DEFAULT_INITIAL_PAYMENT_PCT))
  const [isCustom, setIsCustom] = useState(false)
  const [note, setNote] = useState('')
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!proposalId) { setLoading(false); return }
    getContractTerms(proposalId).then(t => {
      if (cancelled) return
      setPct(String(t.initialPaymentPct))
      setIsCustom(!!t.isCustom)
      setNote(t.note || '')
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [proposalId])

  const n = Number(pct)
  const valid = isFinite(n) && n >= 0 && n <= 100
  // A dollar Retainer Amount wins in the document — the agreement has two mutually exclusive
  // versions of 3.6 and the dollar one is printed whenever an amount is set. Saying so here beats
  // letting someone set 20% and find a dollar figure on the PDF.
  const retainerWins = Number(retainerAmount) > 0

  async function save() {
    if (!valid) { await alertDialog({ title: 'Check the percentage', message: 'The initial payment must be between 0 and 100 percent.' }); return }
    setBusy(true)
    try {
      const t = await saveContractTerms(proposalId, { initialPaymentPct: n, note })
      setIsCustom(!!t.isCustom); setDirty(false)
    } catch (e) {
      await alertDialog({ title: 'Could not save', message: e.message })
    } finally { setBusy(false) }
  }

  async function revert() {
    setBusy(true)
    try {
      const t = await clearContractTerms(proposalId)
      setPct(String(t.initialPaymentPct)); setIsCustom(false); setNote(''); setDirty(false)
    } catch (e) {
      await alertDialog({ title: 'Could not revert', message: e.message })
    } finally { setBusy(false) }
  }

  if (!proposalId) return null

  return (
    <div style={{ ...S.wrap, ...(isCustom ? S.wrapOn : null) }}>
      <div style={S.headRow}>
        <div style={S.head}>
          Agreement terms
          {isCustom && <span style={S.tag}>Negotiated</span>}
        </div>
        {isCustom && (
          <button style={S.linkBtn} onClick={revert} disabled={busy}>Use the standard terms</button>
        )}
      </div>

      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : (
        <>
          <div style={S.row}>
            <label style={F.label}>Initial payment on execution</label>
            <input
              style={{ ...S.pctInput, ...(valid ? null : S.pctBad) }}
              value={pct}
              onChange={e => { setPct(e.target.value.replace(/[^0-9.]/g, '')); setDirty(true) }}
            />
            <span style={S.pctSign}>% of the lump sum fees</span>
          </div>

          <div style={S.hint}>
            Standard is {DEFAULT_INITIAL_PAYMENT_PCT}%. This is Article 3.6 of the agreement and it
            prints in the contract, so set it to what was actually agreed.
          </div>

          {/* The exact sentence the client will read. A contract variable nobody can see before
              sending is a variable nobody checks. */}
          {valid && !retainerWins && (
            <div style={S.preview}>
              <span style={S.previewLabel}>Article 3.6 will read</span>
              An initial payment in the amount of <strong>{fmtPct(n)}</strong> of the lump sum fees
              shall be due upon execution of this agreement to be paid prior to the start of work.
              The initial payment amount will be credited back to Client on the final invoice(s).
            </div>
          )}

          {retainerWins && (
            <div style={S.warn}>
              This proposal also carries a <strong>Retainer</strong> dollar amount, and the agreement
              prints the dollar version of Article 3.6 whenever one is set. Clear the retainer to
              make this percentage the one that appears.
            </div>
          )}

          {n === 0 && valid && (
            <div style={S.warn}>
              Zero means the contract states no initial payment is due. Leave it at{' '}
              {DEFAULT_INITIAL_PAYMENT_PCT} unless that is deliberate.
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <label style={F.label}>Why this was negotiated (internal)</label>
            <input
              style={F.input}
              value={note}
              placeholder="e.g. client agreed to a larger initial payment in exchange for the all-inclusive fee"
              onChange={e => { setNote(e.target.value); setDirty(true) }}
            />
          </div>

          {/* Same reasoning as the all-inclusive panel's banner: Preview PDF renders from the
              SAVED row, so an unsaved number here shows the previous wording with nothing saying so. */}
          {dirty && (
            <div style={S.dirtyBanner}>
              <strong>Not saved.</strong> Preview PDF and the client&apos;s document still show the
              previously saved terms. Save first.
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <button style={F.btnPrimary} onClick={save} disabled={busy || !valid}>
              {busy ? 'Saving…' : dirty ? 'Save agreement terms' : 'Saved'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const S = {
  wrap: { border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, background: 'var(--aj-bg-subtle)' },
  wrapOn: { borderColor: 'rgba(29,158,117,0.45)', background: 'rgba(29,158,117,0.05)' },
  head: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  headRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.5 },
  linkBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 },
  row: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45, marginTop: 4 },
  pctInput: { width: 58, textAlign: 'right', fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text)', padding: '4px 5px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)' },
  pctBad: { borderColor: '#c0392b', color: '#c0392b' },
  pctSign: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' },
  preview: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.5, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '8px 10px', marginTop: 8 },
  previewLabel: { display: 'block', fontFamily: '"DM Sans", sans-serif', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 3 },
  warn: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.45, color: '#8a4b00', background: 'rgba(230,145,0,0.10)', border: '0.5px solid rgba(230,145,0,0.35)', borderRadius: 4, padding: '6px 9px', marginTop: 8 },
  tag: { marginLeft: 7, fontFamily: '"DM Sans", sans-serif', fontSize: 9.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#1f9d57', border: '0.5px solid rgba(29,158,117,0.4)', borderRadius: 20, padding: '1px 6px' },
  dirtyBanner: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.45, color: '#8a4b00', background: 'rgba(230,145,0,0.14)', border: '0.5px solid rgba(230,145,0,0.5)', borderRadius: 4, padding: '7px 10px', marginTop: 12 },
}
